import React from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  UncontrolledTooltip,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import { axiosGet, axiosPost, axiosPut } from "util/apiConfig.js";

// Derived directly from routes.js — update here if a route's roles change.
const ROLE_SECTIONS = {
  sales_support: [
    "Message",
    "WhatsApp Chat",
    "Sales Notification",
    "Sales Follow up",
  ],
  sales: [
    "Sales Notification",
  ],
  support: [
    "Message",
    "WhatsApp Chat",
  ],
  sales_admin: [
    "Message",
    "WhatsApp Chat",
    "Sales Follow up",
  ],
  sub_admin: [
    "Quick View",
    "Dashboard",
    "Class",
    "Message",
    "Bulk Message",
    "WhatsApp Chat",
    "Email Error Logs",
    "Sales Notification",
    "Sales Follow up",
    "User Registration",
    "Private Section",
    "Reports",
    "Belt Update",
    "Makeup Class",
    "Cancelled Session",
    "Teachers",
    "Teacher Calendar",
    "Teachers Hiring",
    "Teachers Report",
    "Demo Teacher Perf.",
    "Curriculum",
    "Category",
    "Email Setting",
    "Age Group",
    "Room",
  ],
  sales_ops: [
    "Quick View",
    "Dashboard",
    "Class",
    "Message",
    "Sales Notification",
    "Sales Follow up",
    "User Registration",
    "Private Section",
    "Reports",
    "Makeup Class",
    "Cancelled Session",
    "Teachers",
    "Teacher Calendar",
    "Demo Teacher Perf.",
    "Curriculum",
  ],
};

const ROLE_OPTIONS = [
  {
    value: "sales_support",
    label: "Sales Support",
    color: "info",
    hint: "For staff who handle both inbound messages and trial class leads.",
  },
  {
    value: "sales",
    label: "Sales",
    color: "success",
    hint: "For staff who only manage trial class leads and follow-ups.",
  },
  {
    value: "support",
    label: "Support",
    color: "warning",
    hint: "For staff who only handle customer messages and WhatsApp chats.",
  },
  {
    value: "sales_admin",
    label: "Sales Admin",
    color: "primary",
    hint: "For senior sales staff who manage follow-ups and messaging.",
  },
  {
    value: "sub_admin",
    label: "Sub Admin",
    color: "dark",
    hint: "Broad access for operations staff. Excludes Payments and Admin Users.",
  },
  {
    value: "sales_ops",
    label: "Sales Ops",
    color: "secondary",
    hint: "Ops + sales access. Excludes bulk/WhatsApp messaging, email settings/templates/logs, belts, teacher hiring/reports, category, age group, room, payments.",
  },
];

const EMPTY_FORM = { name: "", email: "", password: "", userType: "sales_support" };

// Small badge per section name, colored by category
const SECTION_COLOR = (name) => {
  if (["Message", "WhatsApp Chat", "Bulk Message"].includes(name)) return "info";
  if (["Sales Notification", "Sales Follow up"].includes(name)) return "success";
  if (["Dashboard", "Quick View", "Reports", "Demo Teacher Perf."].includes(name)) return "primary";
  if (["Teachers", "Teacher Calendar", "Teachers Hiring", "Teachers Report"].includes(name)) return "warning";
  return "secondary";
};

function PermissionBadges({ userType, inline = false }) {
  const sections = ROLE_SECTIONS[userType] || [];
  if (!sections.length) return <span className="text-muted small">—</span>;
  return (
    <div className={`d-flex flex-wrap${inline ? "" : " mt-2"}`} style={{ gap: "4px" }}>
      {sections.map((s) => (
        <Badge key={s} color={SECTION_COLOR(s)} pill style={{ fontSize: "0.72rem" }}>
          {s}
        </Badge>
      ))}
    </div>
  );
}

class AdminUsers extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      users: [],
      loading: false,
      addModal: false,
      form: { ...EMPTY_FORM },
      saving: false,
      formError: "",
    };
  }

  componentDidMount() {
    this.fetchUsers();
  }

  fetchUsers = () => {
    this.setState({ loading: true });
    axiosGet("auth/admin-users")
      .then((res) => {
        this.setState({
          loading: false,
          users: res.isError ? [] : res.data || [],
        });
      })
      .catch(() => this.setState({ loading: false }));
  };

  openAddModal = () => {
    this.setState({ addModal: true, form: { ...EMPTY_FORM }, formError: "" });
  };

  closeAddModal = () => {
    this.setState({ addModal: false, formError: "" });
  };

  handleFormChange = (field, value) => {
    this.setState((prev) => ({ form: { ...prev.form, [field]: value } }));
  };

  submitAddUser = () => {
    const { form } = this.state;
    if (!form.name || !form.email || !form.password || !form.userType) {
      this.setState({ formError: "All fields are required." });
      return;
    }
    if (form.password.length < 6) {
      this.setState({ formError: "Password must be at least 6 characters." });
      return;
    }
    this.setState({ saving: true, formError: "" });
    axiosPost("auth/admin-users", form)
      .then((res) => {
        if (res.isError) {
          this.setState({
            saving: false,
            formError: res.message || "Failed to create user. Email may already exist.",
          });
        } else {
          this.setState({ saving: false, addModal: false });
          this.fetchUsers();
        }
      })
      .catch(() =>
        this.setState({ saving: false, formError: "Something went wrong." })
      );
  };

  toggleUser = (userId, isActive) => {
    const action = isActive ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    axiosPut("auth/admin-users/toggle", { userId })
      .then((res) => {
        if (!res.isError) this.fetchUsers();
      })
      .catch(() => {});
  };

  render() {
    const { users, loading, addModal, form, saving, formError } = this.state;
    const selectedRole = ROLE_OPTIONS.find((r) => r.value === form.userType);

    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Row>
            <div className="col">
              <Card className="shadow">
                <CardHeader className="border-0">
                  <Row className="align-items-center">
                    <Col>
                      <h3 className="mb-0">Admin Users</h3>
                      <small className="text-muted">
                        Manage panel access for ops/sales staff
                      </small>
                    </Col>
                    <Col className="text-right">
                      <Button color="primary" size="sm" onClick={this.openAddModal}>
                        <i className="ni ni-fat-add mr-1" />
                        Add User
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody style={{ padding: 0 }}>
                  <Table className="align-items-center table-flush" responsive hover>
                    <thead className="thead-light">
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Section Access</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            <Spinner size="sm" color="primary" /> Loading…
                          </td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">
                            No admin users found.
                          </td>
                        </tr>
                      ) : (
                        users.map((u, i) => {
                          const role = ROLE_OPTIONS.find((r) => r.value === u.userType);
                          return (
                            <tr key={u.userId}>
                              <td>{i + 1}</td>
                              <td className="font-weight-bold">{u.name}</td>
                              <td>{u.email}</td>
                              <td>
                                <Badge
                                  color={role ? role.color : "secondary"}
                                  pill
                                  id={`role-${u.userId}`}
                                  style={{ cursor: "default" }}
                                >
                                  {role ? role.label : u.userType}
                                </Badge>
                                {role && (
                                  <UncontrolledTooltip target={`role-${u.userId}`}>
                                    {role.hint}
                                  </UncontrolledTooltip>
                                )}
                              </td>
                              <td style={{ maxWidth: "280px" }}>
                                <PermissionBadges userType={u.userType} inline />
                              </td>
                              <td>
                                {u.isActive ? (
                                  <Badge color="success">Active</Badge>
                                ) : (
                                  <Badge color="danger">Inactive</Badge>
                                )}
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  color={u.isActive ? "danger" : "success"}
                                  outline
                                  onClick={() => this.toggleUser(u.userId, u.isActive)}
                                >
                                  {u.isActive ? "Deactivate" : "Activate"}
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </div>
          </Row>
        </Container>

        {/* Add User Modal */}
        <Modal isOpen={addModal} toggle={this.closeAddModal}>
          <ModalHeader toggle={this.closeAddModal}>Add Admin User</ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label>Name</Label>
                <Input
                  type="text"
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => this.handleFormChange("name", e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={form.email}
                  onChange={(e) => this.handleFormChange("email", e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>Password</Label>
                <Input
                  type="password"
                  placeholder="Min 6 characters"
                  value={form.password}
                  onChange={(e) => this.handleFormChange("password", e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label>Role</Label>
                <Input
                  type="select"
                  value={form.userType}
                  onChange={(e) => this.handleFormChange("userType", e.target.value)}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Input>

                {/* Live permission preview */}
                {selectedRole && (
                  <div
                    className="mt-2 p-3 rounded"
                    style={{ backgroundColor: "#f8f9fe", border: "1px solid #e9ecef" }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <Badge color={selectedRole.color} pill className="mr-2">
                        {selectedRole.label}
                      </Badge>
                      <small className="text-muted">{selectedRole.hint}</small>
                    </div>
                    <small className="text-uppercase font-weight-bold text-muted d-block mb-1">
                      Sections this user can access ({(ROLE_SECTIONS[form.userType] || []).length}):
                    </small>
                    <PermissionBadges userType={form.userType} />
                  </div>
                )}
              </FormGroup>

              {formError && (
                <div className="text-danger small mt-1">{formError}</div>
              )}
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.closeAddModal} disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" onClick={this.submitAddUser} disabled={saving}>
              {saving ? <Spinner size="sm" /> : "Create User"}
            </Button>
          </ModalFooter>
        </Modal>
      </>
    );
  }
}

export default AdminUsers;
